import { Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import {
  maxLength,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useTranslate
} from 'react-admin'
import { useLocation } from 'react-router-dom'
import { useOrganizationSettings } from '../../utils/useOrganizationSettings'

const validateEmail = (value?: string) => {
  if (!value) return 'Verplicht veld'
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(value) ? undefined : 'Ongeldig e-mailadres'
}

interface CollaborationsFormProps {
  edit?: boolean
}

const CollaborationsForm: FC<CollaborationsFormProps> = ({ edit = false }) => {
  const translate = useTranslate()
  const location = useLocation()
  const orgSettings = useOrganizationSettings()
  const searchParams = new URLSearchParams(location.search)
  const filter = searchParams.get('filter')

  let nummerBinnenSysteem: string | undefined
  if (filter) {
    try {
      const parsed = JSON.parse(filter) as unknown as { nummerBinnenSysteem: string }
      nummerBinnenSysteem = parsed.nummerBinnenSysteem || ''
    } catch (e) {
      console.warn('Invalid filter JSON', e)
    }
  }

  const kenmerkSysteem = orgSettings?.organization_type
    ? `xxllnc-${orgSettings.organization_type}`
    : ''

  const defaultValues = useMemo(() => {
    const userName = localStorage.getItem('userName')

    return {
      owner: userName || '',
      status: 'INITIALISATIE',
      nummerBinnenSysteem,
      kenmerkSysteem,
    }
  }, [nummerBinnenSysteem, kenmerkSysteem])

  if (!orgSettings) return null

  return (
    <SimpleForm defaultValues={defaultValues}>
      {defaultValues?.nummerBinnenSysteem && <Typography variant="body1" gutterBottom>
        {translate('resources.collaborations.caseDescription', { name: defaultValues.nummerBinnenSysteem })}
      </Typography>}
      <Typography variant="h6" gutterBottom>
        {translate('resources.collaborations.title')}
      </Typography>
      <TextInput
        source="titel"
        label="data.title"
        fullWidth
        validate={[required(), maxLength(200)]}
      />
      <TextInput
        source="beschrijving"
        label="data.descriptionCooperation"
        fullWidth
        validate={[required(), maxLength(6144)]}
      />
      { !edit && <SelectInput
        source="bronVerzoek"
        label="data.sourceRequest"
        fullWidth
        choices={[
          { id: 'DSO_LV', name: 'DSO_LV' },
          { id: 'OLO', name: 'OLO' }
        ]}
        defaultValue='DSO_LV'
      />}
      { !edit && <SelectInput
        source="samenwerkDoel"
        label="data.purpose"
        fullWidth
        validate={required()}
        choices={[
          { id: 'CONCEPTVERZOEK', name: 'Conceptverzoek' },
          { id: 'BEHANDELEN', name: 'Behandelen' },
          { id: 'TOEZICHT_EN_HANDHAVING ', name: 'Toezicht en handhaving' }
        ]}
        defaultValue='BEHANDELEN'
      />
      }
      { !edit && <SelectInput
        source="samenwerkVorm"
        label="data.collaborationForm"
        fullWidth
        choices={[
          { id: 'SAMENWERKEN_AAN_BEHANDELEN', name: 'Samenwerken aan behandelen' },
          { id: 'SAMENWERKEN_AAN_PLANNEN', name: 'Samenwerken aan plannen' },
          { id: 'SAMENWERKEN_AAN_TOEZICHT_EN_HANDHAVING  ', name: 'Samenwerken aan toezicht en handhaving' }
        ]}
      />}
      { !edit && <SelectInput
        source="typeVerzoek"
        label="data.typeVerzoek"
        fullWidth
        choices={[
          { id: 'Aanvraag vergunning', name: 'Aanvraag vergunning' },
          { id: 'Melding', name: 'Melding' },
          { id: 'Informatie ', name: 'Informatie' },
          { id: 'Informatie ongewoon voorval', name: 'Informatie ongewoon voorval' },
          { id: 'Aanvraag maatwerkvoorschrift', name: 'Aanvraag maatwerkvoorschrift' },
          { id: 'Melding gelijkwaardige maatregel', name: 'Melding gelijkwaardige maatregel' },
          { id: 'Aanvraag toestemming gelijkwaardige maatregel', name: 'Aanvraag toestemming gelijkwaardige maatregel' },
        ]}
        defaultValue='Aanvraag vergunning'
      />}
      { !edit && <TextInput
        source="requestNumber"
        label="data.requestNumber"
        helperText="data.requestNumberHelperText"
      />}
      { !edit && <TextInput
        source="oloVerzoeknummer"
        label="data.oloVerzoeknummer"
        helperText="data.oloRequestNumberHelperText"
      />}
      <TextInput
        source="globaleLocatie"
        label="data.globaleLocatie"
      />
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        {translate('resources.collaborations.contactPersonCompetentAuthority')}
      </Typography>
      <TextInput
        source="contactpersoonNaam"
        label="data.name"
        validate={[required(), maxLength(200)]}
        fullWidth
      />
      <TextInput
        source="contactpersoonTelefoonnummer"
        label="data.phone"
        validate={[required(), maxLength(20)]}
        fullWidth
      />
      <TextInput
        source="contactpersoonEmailadres"
        label="data.email"
        validate={[validateEmail, required(), maxLength(256)]}
        fullWidth
      />
      <Typography variant="h6" gutterBottom>
        {translate('resources.collaborations.sourceSystem')}
      </Typography>
      <TextInput
        source="kenmerkSysteem"
        label="data.name"
        validate={[required(), maxLength(80)]}
      />
      <TextInput
        source="nummerBinnenSysteem"
        label="data.caseNumber"
        validate={[required(), maxLength(80)]}
      />
    </SimpleForm>
  )
}

export default CollaborationsForm
